/**
 * Reconciling the device clock with the server's.
 *
 * A submission carries three times and they are never collapsed into one:
 *
 *   occurredAtDevice   what the phone's clock said when the operator finished
 *   receivedAtServer   when it finally arrived, which for a queued record is wrong
 *   occurredAt         derived and authoritative, computed here
 *
 * The failure this prevents is subtle and would go unnoticed for months: an
 * inspection completed at 06:58 in a freezer and drained at 10:00 must be filed at
 * 06:58. Using the arrival time would show the truck inspected three hours into the
 * shift, and using the raw device clock would trust a phone whose time is whatever
 * its owner set.
 *
 * The offset is snapshotted at CAPTURE, not at drain. By drain time the phone may
 * have re-synced its clock, and applying a newer offset to an older reading produces
 * a number that is wrong in a way nobody can reconstruct.
 */

export interface ClockReading {
  readonly deviceNowMs: number;
  readonly serverNowMs: number;
}

export interface CaptureStamp {
  readonly occurredAtDeviceMs: number;
  readonly clockOffsetMsAtCaptureMs: number;
  /**
   * How old the offset was when it was applied. `Number.POSITIVE_INFINITY` for a
   * device that has never successfully reached the server.
   */
  readonly offsetAgeMs: number;
}

export type TimeConfidence = 'normal' | 'low';

export interface ResolvedTime {
  readonly occurredAtMs: number;
  readonly confidence: TimeConfidence;
}

const DAY_MS = 86_400_000;

/**
 * Beyond a week, a phone has almost certainly re-synced, been rebooted, or crossed a
 * time zone. The reading is still kept -- it is the operator's record -- but it is
 * marked so the manager UI can say "reported time approximate" rather than asserting
 * a precision we do not have.
 */
export const STALE_OFFSET_MS = 7 * DAY_MS;

export function measureOffsetMs(reading: ClockReading): number {
  return reading.serverNowMs - reading.deviceNowMs;
}

export function resolveOccurredAt(stamp: CaptureStamp): ResolvedTime {
  return {
    occurredAtMs: stamp.occurredAtDeviceMs + stamp.clockOffsetMsAtCaptureMs,
    confidence: stamp.offsetAgeMs > STALE_OFFSET_MS ? 'low' : 'normal',
  };
}
